
import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share2, X, Copy, Download, Link } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/components/ui/use-toast";
import { getAnimalInfo, getGroupFromNumber } from "@/utils/animalImages";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export type PredictionType = "milhar" | "centena" | "grupo" | "duque" | "terno";

type SharePredictionDialogProps = {
  open: boolean;
  onClose: () => void;
  predictions: string[];
  predictionType: PredictionType;
  title: string;
};

const SharePredictionDialog = ({ 
  open, 
  onClose, 
  predictions,
  predictionType,
  title
}: SharePredictionDialogProps) => {
  const [activeTab, setActiveTab] = useState<"view" | "image">("view");
  const shareImageRef = useRef<HTMLDivElement>(null);
  
  // Função para copiar texto para a área de transferência
  const handleShareText = () => {
    const shareText = `Meus palpites de ${predictionType}: ${predictions.join(", ")}`;
    navigator.clipboard.writeText(shareText)
      .then(() => {
        toast({
          title: "Sucesso!",
          description: "Texto copiado para a área de transferência!",
        });
      })
      .catch(err => {
        console.error('Erro ao copiar texto: ', err);
        toast({
          title: "Erro",
          description: "Não foi possível copiar o texto",
          variant: "destructive"
        });
      });
  };

  // Função para capturar e baixar a imagem
  const handleDownloadImage = async () => {
    try {
      // Verificar se a referência para o elemento está disponível
      if (!shareImageRef.current) {
        toast({
          title: "Erro",
          description: "Não foi possível gerar a imagem",
          variant: "destructive"
        });
        return;
      }

      // Importar html2canvas dinamicamente
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;
      
      const canvas = await html2canvas(shareImageRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      // Criar um link para download
      const link = document.createElement('a');
      link.download = `palpites-${predictionType}-${new Date().getTime()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast({
        title: "Sucesso!",
        description: "Imagem baixada com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao gerar imagem:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar a imagem para download",
        variant: "destructive"
      });
    }
  };

  // Função para compartilhar a imagem via Web Share API
  const handleShareImage = async () => {
    try {
      if (!shareImageRef.current) {
        toast({
          title: "Erro",
          description: "Não foi possível gerar a imagem para compartilhar",
          variant: "destructive"
        });
        return;
      }

      // Importar html2canvas dinamicamente
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;
      
      const canvas = await html2canvas(shareImageRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });

      // Converter canvas para blob
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob as Blob);
        }, 'image/png', 0.8);
      });

      // Verificar se a API de compartilhamento está disponível
      if (navigator.share) {
        const file = new File([blob], `palpites-${predictionType}.png`, { type: 'image/png' });
        
        await navigator.share({
          title: `Palpites de ${predictionType.toUpperCase()}`,
          text: `Meus palpites de ${predictionType} gerados pelo PALPITE GURU`,
          files: [file]
        });
        
        toast({
          title: "Compartilhado!",
          description: "Imagem compartilhada com sucesso!",
        });
      } else {
        // Fallback para dispositivos sem a API de compartilhamento
        const url = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `palpites-${predictionType}-${new Date().getTime()}.png`;
        link.href = url;
        link.click();
        
        toast({
          title: "Imagem pronta",
          description: "A imagem foi baixada para o seu dispositivo.",
        });
      }
    } catch (error) {
      console.error('Erro ao compartilhar imagem:', error);
      toast({
        title: "Erro",
        description: "Não foi possível compartilhar a imagem",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="p-0 overflow-hidden max-w-[95vw] w-[700px] max-h-[90vh]">
        <div className="relative flex flex-col">
          <DialogHeader className="p-6 border-b">
            <DialogTitle className="text-2xl font-bold">
              Compartilhar: {title}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Compartilhe seus palpites com amigos
            </DialogDescription>
          </DialogHeader>
          
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute top-4 right-4" 
            onClick={onClose}
          >
            <X size={18} />
          </Button>
          
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "view" | "image")} className="w-full">
            <div className="px-6 pt-4">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="view">Visualizar</TabsTrigger>
                <TabsTrigger value="image">Imagem Compartilhável</TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="view" className="m-0">
              <div className="overflow-auto p-6 max-h-[60vh]">
                <div className="grid grid-cols-1 gap-4 mb-6">
                  {predictions.map((prediction, index) => {
                    const animalInfo = getAnimalInfo(prediction, predictionType);
                    
                    return (
                      <div 
                        key={index}
                        className="bg-background border border-border rounded-xl shadow-sm p-4"
                      >
                        <div className="flex flex-col md:flex-row gap-4">
                          <div className="w-full md:w-1/4 flex justify-center items-center">
                            <div className="w-16 h-16 rounded-full bg-yellow-300 border-2 border-black flex items-center justify-center shadow-md">
                              <span className="text-3xl font-black text-black">{animalInfo.firstLetter}</span>
                            </div>
                          </div>
                          
                          <div className="flex-1">
                            <h3 className="text-2xl font-bold mb-2">{prediction}</h3>
                            <p className="text-lg mb-1 capitalize">Tipo: {predictionType}</p>
                            
                            {(predictionType === "milhar" || predictionType === "centena" || predictionType === "grupo") && (
                              <div className="mt-2">
                                <p className="text-sm text-muted-foreground">
                                  Grupo: <span className="font-medium">{getGroupFromNumber(prediction) || prediction}</span>
                                </p>
                                <p className="text-base font-medium">
                                  Animal: <span className="text-primary">{animalInfo.name}</span>
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="image" className="m-0">
              <div className="p-6 overflow-auto">
                <div 
                  ref={shareImageRef}
                  className="p-6 bg-white rounded-xl border border-border shadow-lg max-w-3xl mx-auto"
                >
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold">Palpites IA GURU - {predictionType.charAt(0).toUpperCase() + predictionType.slice(1)}</h2>
                    <p className="text-sm text-muted-foreground">Gerado em: {new Date().toLocaleDateString()}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-8">
                    {predictions.map((prediction, index) => {
                      const animalInfo = getAnimalInfo(prediction, predictionType);
                      
                      return (
                        <div 
                          key={index}
                          className="bg-background border border-border rounded-lg p-3 shadow-sm text-center"
                        >
                          <div className="mb-2 h-16 flex items-center justify-center">
                            <div className="w-14 h-14 rounded-full bg-yellow-300 border-2 border-black flex items-center justify-center">
                              <span className="text-2xl font-black text-black">{animalInfo.firstLetter}</span>
                            </div>
                          </div>
                          <div className="text-xl font-bold">{prediction}</div>
                          {(predictionType === "milhar" || predictionType === "centena" || predictionType === "grupo") && (
                            <div className="text-xs mt-1">{animalInfo.name}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="text-center text-xs text-muted-foreground mt-4">
                    <p>palpiteguru.com.br - Os melhores palpites gerados por IA</p>
                  </div>
                </div>
                
                <div className="flex justify-center mt-6 gap-3 flex-wrap">
                  <Button onClick={handleDownloadImage}>
                    <Download size={18} className="mr-2" />
                    Baixar Imagem
                  </Button>
                  <Button onClick={handleShareImage} className="btn-gradient">
                    <Share2 size={18} className="mr-2" />
                    Compartilhar Imagem
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="p-6 border-t">
            <div className="flex flex-wrap justify-center gap-3">
              <Button onClick={handleShareText}>
                <Copy size={18} className="mr-2" />
                Copiar Texto
              </Button>
              <Button onClick={() => setActiveTab("image")} variant="outline">
                <Link size={18} className="mr-2" />
                Gerar Imagem
              </Button>
              <Button onClick={handleShareImage} className="btn-gradient">
                <Share2 size={18} className="mr-2" />
                Compartilhar Imagem
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SharePredictionDialog;
